import { FlowState, OAuth2State } from './interfaces';
import { UserRole } from '../../util/types';

/**
 * The Authorizer class manages and verifies the OAuth2 and session states of a user.
 * It provides methods to check if a session is active, refreshable, or if the user has specific roles.
 */
export class Authorizer {
  private readonly authState: OAuth2State;

  constructor(authState: OAuth2State) {
    this.authState = authState;
  }

  /**
   * Checks if the user's session is currently active.
   *
   * @returns A boolean value indicating if the session is active.
   */
  public isActive(): boolean {
    return this.hasActiveSession();
  }

  /**
   * Checks if the user's session is refreshable but not currently active.
   *
   * @returns A boolean value indicating if the session is refreshable but not active.
   */
  public isIdle(): boolean {
    return this.hasRefreshableSession() && !this.hasActiveSession();
  }

  /**
   * Checks if the user is not anonymous by verifying if there is any active or refreshable session.
   *
   * @returns A boolean value indicating whether the user is not anonymous (true) or anonymous (false).
   */
  public isNotAnonymous(): boolean {
    return this.hasAnySession();
  }

  /**
   * Checks if the current user is anonymous by verifying if there is no active or refreshable session.
   *
   * @returns A boolean value indicating whether the user is anonymous (true) or not (false).
   */
  public isAnonymous(): boolean {
    return !this.hasAnySession();
  }

  /**
   * Checks if the user has the required role.
   *
   * @param requiredRole - The role to check for.
   * @returns A boolean indicating if the user has the required role.
   */
  public hasRole(requiredRole: UserRole): boolean {
    return this.authState?.session?.roles?.includes(requiredRole);
  }

  /**
   * Checks if the current user has any of the specified roles.
   * If the user is anonymous, it checks if the specified roles include 'ROLE_GUEST'.
   * Otherwise, it verifies if any of the user's roles match the provided roles.
   *
   * @param roles - An array of strings representing the roles to check against the user's roles.
   * @returns A boolean indicating if the user has any of the specified roles.
   */
  public hasAnyOfRoles(roles: UserRole[]): boolean {
    if (this.isAnonymous()) {
      return roles.includes('ROLE_GUEST');
    }
    return this.authState?.session?.roles?.some((role: string) => roles.includes(role)) ?? false;
  }

  public hasRoleOrHigher(role: UserRole): boolean {
    if (role === 'ROLE_GUEST') {
      return true;
    }
    if (role === 'ROLE_USER') {
      return this?.hasAnyOfRoles(['ROLE_USER', 'ROLE_EDITOR', 'ROLE_ADMIN']);
    }
    if (role === 'ROLE_EDITOR') {
      return this?.hasAnyOfRoles(['ROLE_EDITOR', 'ROLE_ADMIN']);
    }
    if (role === 'ROLE_ADMIN') {
      return this.authState?.session?.roles?.includes('ROLE_ADMIN');
    }
  }

  public getFlow(): FlowState {
    return this.authState.flow;
  }

  /**
   * Checks if the user's session is currently active.
   *
   * @returns A boolean value indicating if the session is active.
   */
  private hasActiveSession(): boolean {
    return this.authState?.token?.auth?.expires >= Date.now();
  }

  /**
   * Checks if the user's session is refreshable.
   *
   * @returns A boolean value indicating if the session is refreshable.
   */
  private hasRefreshableSession(): boolean {
    return this.authState?.token?.refresh?.expires >= Date.now();
  }

  /**
   * Checks if the user's session is active or refreshable.
   *
   * @returns A boolean value indicating if the session is active or refreshable.
   */
  private hasAnySession(): boolean {
    return this.hasActiveSession() || this.hasRefreshableSession();
  }
}
